import type { AllarmiPaginationDTO } from '@/models/dto/allarme/allarme-pagination-dto'
import type { AllarmeSconosciutoDTO } from '@/models/dto/allarme/allarme-sconosciuto-dto'
import { mapToAlarms } from '@/models/mappers/alarm/alarms-mapper'
import { mapToUnknownAlarm } from '@/models/mappers/alarm/unknows-alarm-mapper'
import type { AlarmsWithPagination } from '@/models/types/alarm/alarms-with-pagination'
import type { UnknownAlarm } from '@/models/types/alarm/unknown-alarm'
import type { OrderType } from '@/types/table'
import { capitalizeFirstLetter } from '@/utils/utils'
import { makeRequest, type MakeRequestOptions } from './make-request'

type AlarmParams = {
    order?: OrderType
    sort?: keyof AllarmeSconosciutoDTO
    search?: string
    page?: string
    value?: string
} & Partial<Record<keyof UnknownAlarm, string>>

type AlarmCrudType = () => {
    getOne: (id: number, options?: MakeRequestOptions) => Promise<UnknownAlarm>
    getAll: (options?: MakeRequestOptions) => Promise<UnknownAlarm[]>
    getMany: (
        itemsPerPage?: string,
        params?: AlarmParams,
        onlyEnabled?: boolean,
        options?: MakeRequestOptions,
    ) => Promise<AlarmsWithPagination>
    getManyActive: (
        itemsPerPage?: string,
        params?: Omit<AlarmParams, 'archived' | 'state'>,
        options?: MakeRequestOptions,
    ) => Promise<AlarmsWithPagination>
    getManyDismissed: (
        itemsPerPage?: string,
        params?: Omit<AlarmParams, 'archived' | 'state'>,
        options?: MakeRequestOptions,
    ) => Promise<AlarmsWithPagination>
    getManyArchived: (
        itemsPerPage?: string,
        params?: Omit<AlarmParams, 'archived' | 'state'>,
        options?: MakeRequestOptions,
    ) => Promise<AlarmsWithPagination>
    // post: (alarm: NewAlarm) => Promise<Alarm>
    // put: (alarm: Alarm) => Promise<Alarm>
    delete: (id: number, options?: MakeRequestOptions) => Promise<void>
    dismiss: (id: number, note?: string, options?: MakeRequestOptions) => Promise<void>
    archive: (id: number, options?: MakeRequestOptions) => Promise<void>
    restore: (id: number, options?: MakeRequestOptions) => Promise<void>
    share: (id: number, options?: MakeRequestOptions) => Promise<void>
}

const ALARM_URL =
    typeof window === 'undefined'
        ? `${process.env.API_URL}/api/v1/Allarmi`
        : `${location.origin}${process.env.NEXT_PUBLIC_BASEPATH}/api/v1/Allarmi`
const ALARM_TAG = 'alarm'

const getMany: (
    itemsPerPage?: string,
    params?: AlarmParams,
    onlyEnabled?: boolean,
    options?: MakeRequestOptions,
) => Promise<AlarmsWithPagination> = async (itemsPerPage, params, onlyEnabled, options) => {
    const url = new URL(`${ALARM_URL}/Pagination`)
    url.searchParams.append('PageSize', itemsPerPage ?? '999999')
    if (params?.page) url.searchParams.append('PageIndex', params.page)
    if (params?.order) url.searchParams.append('Descending', params.order === 'desc' ? 'true' : 'false')
    if (params?.sort) url.searchParams.append('SortBy', capitalizeFirstLetter(params.sort))

    if (onlyEnabled) url.searchParams.append('OnlyActive', 'true')

    if (params?.search) url.searchParams.append('Filters[search]', params.search)

    if (params?.sensorName) url.searchParams.append('Filters[AliasIdSensore]', params.sensorName)
    if (params?.description) url.searchParams.append('Filters[Descrizione]', params.description)
    if (params?.state) url.searchParams.append('Filters[Stato]', params.state)
    if (params?.note) url.searchParams.append('Filters[Note]', params.note)
    if (params?.timestampstart) url.searchParams.append('Filters[TimestampStart]', params.timestampstart)
    if (params?.timestamplast) url.searchParams.append('Filters[TimestampLast]', params.timestamplast)
    if (params?.maxThreshold) url.searchParams.append('Filters[RMaxThreshold]', params.maxThreshold)
    if (params?.minThreshold) url.searchParams.append('Filters[RMinThreshold]', params.minThreshold)
    if (params?.value) url.searchParams.append('Filters[Value]', params.value)
    if (params?.ackTimestamp) url.searchParams.append('Filters[TimestampAck]', params.ackTimestamp)
    if (params?.facilityId) url.searchParams.append('Filters[IdImpianto]', params.facilityId)
    if (params?.userId) url.searchParams.append('Filters[IdUtente]', params.userId)
    if (params?.sensorId) url.searchParams.append('Filters[IdSensore]', params.sensorId)
    if (params?.archived) url.searchParams.append('Filters[Archiviato]', params.archived)
    if (params?.shared) url.searchParams.append('Filters[Condiviso]', params.shared)

    const allarmiPaginationDTO = await makeRequest<AllarmiPaginationDTO>(url, {
        method: 'GET',
        next: { tags: [ALARM_TAG] },
        ...options,
    })
    const facilitiesWithPagination = mapToAlarms(allarmiPaginationDTO)
    return facilitiesWithPagination
}

const alarmCRUD: AlarmCrudType = () => ({
    getOne: async (id, options) => {
        const url = new URL(`${ALARM_URL}/${id.toString()}`)
        const allarmeDTO = await makeRequest<AllarmeSconosciutoDTO>(url, {
            method: 'GET',
            next: { tags: [ALARM_TAG] },
            ...options,
        })
        const alarm = mapToUnknownAlarm(allarmeDTO)
        return alarm
    },
    getAll: async (options) => {
        const url = new URL(ALARM_URL)
        const allarmeDTOs = await makeRequest<AllarmeSconosciutoDTO[]>(url, {
            method: 'GET',
            next: { tags: [ALARM_TAG] },
            ...options,
        })
        const alarms = allarmeDTOs.map((allarmeDTO) => mapToUnknownAlarm(allarmeDTO))
        return alarms
    },
    getMany,
    getManyActive: async (itemsPerPage, params, _options) =>
        getMany(itemsPerPage, { ...params }, true, { method: 'GET', cache: 'no-cache' }),
    getManyDismissed: async (itemsPerPage, params, _options) =>
        getMany(itemsPerPage, { ...params, state: '0', archived: 'false' }, false, {
            method: 'GET',
            cache: 'no-cache',
        }),
    getManyArchived: async (itemsPerPage, params, _options) =>
        getMany(itemsPerPage, { ...params, archived: 'true' }, false, { method: 'GET', cache: 'no-cache' }),
    // post: async (newAlarm) => {
    //     const nuovoAllarmeDTO = mapToNuovoAllarmeDTO(newAlarm)
    //     const url = new URL(ALARM_URL)
    //     const allarmeDTOResponse = await postJsonRequest<AllarmeDTO>(url, { body: nuovoAllarmeDTO, ...fetchOptions })
    //     const alarm = mapToAlarm(allarmeDTOResponse)
    //     return alarm
    // },
    // put: async (alarm) => {
    //     const allarmeDTO = mapToAllarmeDTO(alarm)
    //     const url = new URL(ALARM_URL)
    //     const allarmeDTOResponse = await putJsonRequest<AllarmeDTO>(url, { body: allarmeDTO, ...fetchOptions })
    //     const updatedAlarm = mapToAlarm(allarmeDTOResponse)
    //     return updatedAlarm
    // },
    delete: async (id, options) => {
        const url = new URL(`${ALARM_URL}/${id.toString()}`)
        await makeRequest(url, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, ...options })
    },
    dismiss: async (id, note, options) => {
        const url = new URL(`${ALARM_URL}/UpdateAct/${id.toString()}`)
        await makeRequest(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            ...(note && { body: note }),
            ...options,
        })
    },
    archive: async (id, options) => {
        const url = new URL(`${ALARM_URL}/AchiviaAllarme/${id.toString()}/true`)
        await makeRequest(url, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, ...options })
    },
    restore: async (id, options) => {
        const url = new URL(`${ALARM_URL}/AchiviaAllarme/${id.toString()}/false`)
        await makeRequest(url, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, ...options })
    },
    share: async (id, options) => {
        const url = new URL(`${ALARM_URL}/CondividiAllarme/${id.toString()}`)
        await makeRequest(url, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, ...options })
    },
})

export { ALARM_TAG, alarmCRUD, type AlarmCrudType, type AlarmParams as AlarmGetOptions }
